<template>
    <common-content-wrapper class="offer-details" width="gigantic">
        <common-content-spacer height="medium">
            <public-profile-header
                :ratings="ratings"
                :numberOfRatings="offer.numberOfRatings"
                :numberOfViews="offer.numberOfViews"
                :averageRating="offer.averageRating"
                :company="offer.location?.companyServiceProvider"
                :user="offer.location?.userServiceProvider"
                :title="getTranslation(offer?.titleTextContent)"
                :showFavorites="!$auth.isSubdomainActive() && $auth.isLoggedIn()"
                :buttonLabel="$t('common.microcopy.check_availability')"
                :isFavorites="isFavorites"
                :shareTitle="getTranslation(offer?.titleTextContent)"
                :shareDescription="getTranslation(offer?.descriptionTextContent)"
                @add-to-favorites-click="addToFavorites()"
                @action-click="goToAvailability()"
            >
                {{ $t('common.microcopy.by') }}
                <common-button
                    type="link-secondary"
                    :isSubmittable="false"
                    size="tiny"
                    :label="offerOwner"
                    :route="ownerProfileRoute"
                ></common-button>
                <common-verify-badge
                    class="public-profile-header__top-bar--left-side-copy"
                    size="medium"
                    :user="offer?.location?.userServiceProvider"
                ></common-verify-badge>
            </public-profile-header>

            <image-slideshow v-if="offer.photos.length" :images="offer.photos"></image-slideshow>

            <offer-category-component
                v-if="offer.offerCategories.length > 1"
                :offer="offer"
                :title="$t('pages.offer.create_offer.category_types')"
                :showMore="offer.offerCategories.length > 1"
                :showMoreTitle="showMoreTitle"
                @categories-select="selectCategory"
                @check-availability="emitEvent({ name: 'check-availability' })"
            ></offer-category-component>

            <div class="offer-details__about-container">
                <div class="offer-details__about-description">
                    <div v-if="offer?.titleTextContent?.originalText" class="offer-details__about-title">
                        {{ $t('common.microcopy.about') }} {{ getTranslation(offer?.titleTextContent) }}
                    </div>
                    <div>
                        {{ getTranslation(offer?.descriptionTextContent) }}
                    </div>
                </div>
                <meet-the-team :users="availabilityUsers" :sliceDescription="true"></meet-the-team>
            </div>

            <user-ratings
                v-if="offer.numberOfRatings"
                :numberOfRatings="offer.numberOfRatings"
                :averageRating="offer.averageRating"
                :ratings="ratings"
            ></user-ratings>

            <contact-us-segment :markers="offerMarkers" :locations="[offer.location]"></contact-us-segment>
        </common-content-spacer>
    </common-content-wrapper>
</template>

<script>
    import ImageSlideshow from 'components/page-elements/images/ImageSlideshow'
    import ContactUsSegment from 'components/page-elements/common/ContactUsSegment'
    import PublicProfileHeader from 'components/page-elements/common/PublicProfileHeader'
    import MeetTheTeam from 'components/page-elements/common/MeetTheTeam'
    import UserRatings from 'components/page-elements/rating/UserRatings'
    import RoutesEnum from 'enums/routes.enum'
    import RatingAPI from 'api/RatingAPI'
    import OfferAPI from 'api/OfferAPI'
    import OfferCategoryComponent from 'components/page-elements/offer/OfferCategoryComponent'
    import AvailabilityUserRoleEnum from 'enums/availability_user_role.enum'
    import TranslateMixin from 'mixins/TranslateMixin'

    export default {
        mixins: [TranslateMixin],

        components: { ImageSlideshow, OfferCategoryComponent, PublicProfileHeader, MeetTheTeam, ContactUsSegment, UserRatings },

        props: {
            offer: { type: Object, required: true },
        },

        data: () => ({
            ratings: [],
            isFavorites: false,
        }),

        computed: {
            showMoreTitle() {
                return this.offer.offerCategories.length > 3
                    ? this.$t('common.microcopy.show_all')
                    : this.$t('pages.offer.single_offer.tabs.availability')
            },
            isCompanyOfferOwner() {
                return this.offer.location?.companyServiceProvider
            },

            isUserOfferOwner() {
                return this.offer.location?.userServiceProvider
            },

            offerOwner() {
                return this.offer?.location?.companyServiceProvider?.name ?? this.offer?.location?.userServiceProvider?.firstName
            },

            ownerProfileRoute() {
                if (this.isCompanyOfferOwner) {
                    return {
                        name: RoutesEnum.COMPANY.PROFILE,
                        params: { id: this.isCompanyOfferOwner?.id, companyType: 'profile' },
                    }
                }
                return { name: RoutesEnum.USER.PROFILE, params: { id: this.isUserOfferOwner?.id, tab: 'profile' } }
            },

            availabilityUsers() {
                const users = []

                this.offer?.availabilityOffers?.forEach((ao) =>
                    ao.availability.availabilityUsers.forEach((user) => {
                        if (user.function === AvailabilityUserRoleEnum.PERFORMER) {
                            const existingUser = users.find((u) => user.user.id === u.id)
                            if (!existingUser) {
                                const isBlocked = this.offer.blockedShowingPublicProfileUserIds?.includes(user.user.id)
                                users.push({
                                    ...user.user,
                                    showPublicProfile: !isBlocked,
                                })
                            }
                        }
                    }),
                )
                return users
            },
            offerMarkers() {
                const providerData = this.offer.location?.companyServiceProvider || this.offer.location?.userServiceProvider
                return providerData
                    ? [
                          {
                              averageRating: this.offer.averageRating,
                              numberOfRatings: this.offer.numberOfRatings,
                              isCompanyOffer: !!this.offer.location?.companyServiceProvider,
                              providerData,
                              coordinates: { lat: this.offer?.location?.latitude, lng: this.offer?.location?.longitude },
                          },
                      ]
                    : []
            },
        },

        methods: {
            getRatingsForOffer() {
                RatingAPI.getRatingsForOffer({ offerId: this.offer.id, take: 25, skip: 0 }).then((res) => (this.ratings = res))
            },

            goToAvailability() {
                this.emitEvent({ name: 'check-availability' })
            },

            addToFavorites() {
                if (this.isFavorites) {
                    OfferAPI.removeFromFavorites(this.offer.id).then(() => {
                        this.isFavorites = !this.isFavorites
                    })
                } else {
                    OfferAPI.addToFavorites(this.offer.id).then(() => {
                        this.isFavorites = !this.isFavorites
                    })
                }
                this.emitEvent({ name: 'favorites-changed' })
            },

            selectCategory(category) {
                this.emitEvent({ name: 'check-availability', payload: category })
            },
        },

        mounted() {
            this.isFavorites = this.offer.markedAsFavourite
            this.getRatingsForOffer()
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/components/offers/_offer-details.scss';
</style>
